import React from 'react'
import { connect, useDispatch } from 'react-redux'
import { useHistory } from 'react-router'

import FirebaseControlQuery from '@src/firebase-util/firebase-control-query'

import {
  BuildingInterface,
  setBuilding,
  setByFlag,
} from '@store/actionSlices/building'
import { setFilter } from '@store/actionSlices/unitFilter'
import {
  ExtendedUnitFilterOptions,
  ProjectIdentity,
  RootStateTypeExtra,
  SessionMap,
} from '@store/types'

import Container from '@components/container'
import DataHandler from '@components/data-handler'
import { DataNotFound } from '@components/data-handler/errors'

import { Level, useGetBuildingQuerySilent } from '@api/building'
import { SnaploaderConfigurationInterface } from '@api/config'

import useScript from '@utilities/adgroup-utilities/hooks/script'

import filterUnit from '@adUtilities/filter-unit'
import { Unit } from '@adUtilities/types/apartment'

import {
  addHighlight,
  findNodeByName,
  getCameraOrientation,
  handleLoadingState,
  removeHighlight,
  snaploaderJsScript,
} from './snaploader-util'

interface SnapDataInterface {
  scene: any
  virtualTourSteps: Array<any>
  originalInteractiveNodes: Array<any>
}

interface PagePropsInterface {
  session: SessionMap | undefined
  projectIdentity: ProjectIdentity
  snaploaderConfiguration: SnaploaderConfigurationInterface
  building: BuildingInterface
  showPrice: boolean
  unitFilter: ExtendedUnitFilterOptions
  availableStatusLabel: string
}

function SnaploaderView({
  session,
  projectIdentity,
  snaploaderConfiguration,
  building,
  showPrice,
  unitFilter,
  availableStatusLabel,
}: PagePropsInterface) {
  const firebaseControlQuery = FirebaseControlQuery({
    ...projectIdentity,
    sessionKeys: projectIdentity.sessionId,
  })

  const dispatch = useDispatch()
  const history = useHistory()

  const containerRef = React.useRef<HTMLDivElement>(null)
  const lastDispatchedTimestamp = React.useRef<number>()

  const [isSessionConnected, setIsSessionConnected] = React.useState(false)
  const [activeBlock, setActiveBlock] = React.useState('')
  const [activeView, setActiveView] = React.useState('')
  const [snaploaderSDKLoadError, setSnaploaderSDKLoadError] =
    React.useState(false)
  const [viewer, setViewer] = React.useState<any>(null)
  const [snapData, setSnapData] = React.useState<SnapDataInterface>()
  const [isSnapDataLoaded, setIsSnapDataLoaded] = React.useState(false)

  const [currentHighlightedNodes, setCurrentHighlightedNodes] = React.useState<
    Array<any>
  >([])

  const buildingPayload = useGetBuildingQuerySilent({
    projectName: projectIdentity.projectName,
  })

  useScript(snaploaderJsScript, {
    onLoadCallback: () => {
      if (containerRef.current && !viewer) {
        const { sceneSnapId, sceneModelId } = snaploaderConfiguration
        setViewer(
          new window.snaploader.Viewer3d(containerRef.current, {
            sceneSnapId,
            sceneModelId,
          })
        )
      }
    },
    onErrorCallback: (err: any) => {
      setSnaploaderSDKLoadError(true)
      console.error('Snaploader SDK failed to load!', err)
    },
  })

  const prepareSnapDataOnScreenLoad = () => {
    let originalInteractiveNodes: any = {}

    if (viewer.pluginOptions?.developerStack?.interactiveNodes) {
      originalInteractiveNodes = JSON.parse(
        JSON.stringify(viewer.pluginOptions?.developerStack?.interactiveNodes)
      )
    }

    let virtualTourSteps: any = {}

    if (viewer.pluginOptions?.virtualTour?.steps) {
      virtualTourSteps = JSON.parse(
        JSON.stringify(viewer.pluginOptions.virtualTour.steps)
      )
    }

    setIsSnapDataLoaded(true)

    setSnapData({
      scene: viewer.scene,
      virtualTourSteps,
      originalInteractiveNodes,
    })
  }

  const getLevelList = () => {
    if (!snapData?.originalInteractiveNodes) return []

    const levelList: Array<{ key: any; value: any }> = []
    Object.keys(snapData.originalInteractiveNodes).forEach((x: any) => {
      const itNode = snapData.originalInteractiveNodes[x]
      levelList.push({
        key: x,
        value: itNode.mouseOverText ? itNode.mouseOverText : x,
      })
    })
    return levelList
  }

  const getViewList = () => {
    if (!snapData?.virtualTourSteps) return []

    const viewList: Array<string> = []
    Object.keys(snapData.virtualTourSteps).forEach((x: any) => {
      const itNode = snapData.virtualTourSteps[x]
      viewList.push(itNode.title ? itNode.title : x)
    })
    return viewList
  }

  const getCorrectBlockValue = (
    buildingData: BuildingInterface,
    block: string,
    activeLevel: string
  ) => {
    const level = buildingData.levels.find(
      (lvl: Level) => activeLevel === lvl.level
    )
    if (!level) {
      return ''
    }
    const unit = level.data.find(
      (unt: Unit) => (unt?.blockId || '').replace(/\s/g, '') === block
    )
    if (!unit) {
      return ''
    }
    return unit.blockId || ''
  }

  const resetHighlightedNodes = () => {
    if (currentHighlightedNodes.length === 0) return

    currentHighlightedNodes.forEach((node: any) => {
      removeHighlight(node)
      viewer.invalidate()
    })
    setCurrentHighlightedNodes([])
  }

  const highlightLevels = (filteredLevelList: Array<string>) => {
    const levelList = getLevelList()

    filteredLevelList.forEach((filteredLevel: string) => {
      const foundLevel = levelList.find(
        (levelObj) => levelObj.value === filteredLevel
      )

      if (!foundLevel) return

      const targetNode = findNodeByName(snapData?.scene, foundLevel.key)

      if (targetNode) {
        addHighlight(targetNode)
        setCurrentHighlightedNodes((prevState) => [...prevState, targetNode])
        viewer.invalidate()
      }
    })
  }

  const getFilteredData = (): Array<string> => {
    const filteredLevelList: Array<string> = []

    const { levels } = building

    levels.forEach((level: Level) => {
      let units: Array<Unit> = level.data

      if (activeBlock !== '') {
        units = units.filter((unit: Unit) => {
          const blockId = unit.blockId ? unit.blockId.replace(/\s/g, '') : ''
          return blockId === activeBlock
        })
      }

      units.forEach((unit: Unit) => {
        let identifier = ''

        if (unit.blockId && unit.blockId !== '') {
          identifier += `${unit.blockId.replace(/\s/g, '')}_`
        }

        identifier += level.level

        if (!filteredLevelList.includes(identifier)) {
          const response = unitFilter.apply
            ? filterUnit(unit, unitFilter, showPrice, availableStatusLabel)
            : true
          if (response) {
            filteredLevelList.push(identifier)
          }
        }
      })
    })

    return filteredLevelList
  }

  const autoRotateCamera = (state: boolean) => {
    if (viewer) {
      viewer.setAutoRotate(state)
    }
  }

  const setView = (value: string) => {
    const { apply } = unitFilter

    if (!apply) {
      resetHighlightedNodes()
    }

    if (!snapData?.virtualTourSteps) return

    const viewList = getViewList()
    const index = viewList.findIndex((item: string) => item === value)
    const targetStep = snapData.virtualTourSteps[index]

    if (!targetStep) return

    const cameraOrientation = getCameraOrientation(targetStep)
    viewer.setCameraOrientation(
      cameraOrientation.endPosition,
      cameraOrientation.endRotation,
      cameraOrientation.endTargetDistance
    )
  }

  const goToBuilding = (buildingData: BuildingInterface, value: string) => {
    const [block, level] = value.split('_')

    dispatch(
      setByFlag({
        flag: 'activeBlock',
        value: getCorrectBlockValue(buildingData, block, level),
      })
    )

    dispatch(
      setByFlag({
        flag: 'activeLevel',
        value: level || '',
      })
    )

    if (level) {
      history.push('building')
      return
    }

    console.error('Incorrect building data.')
  }

  const handleOnLevelClick = React.useCallback(
    (event: MessageEvent) => {
      try {
        const { name } = JSON.parse(event.data)
        if (name) {
          goToBuilding(building, name)
        }
      } catch (error) {
        console.log('Incorrect format was given in mapping.')
      }
    },
    [building]
  )

  React.useEffect(
    () => () => {
      if (viewer) {
        viewer.removeListener('sceneLoaded', prepareSnapDataOnScreenLoad)
      }

      if (session) {
        firebaseControlQuery.update({
          [`snaploader.isLoaded`]: false,
          [`snaploader.data.views`]: [],
          [`snaploader.data.levels`]: [],
        })
      }

      window.parent.removeEventListener('message', handleOnLevelClick)
    },
    []
  )

  React.useEffect(() => {
    const { building: buildingData, blockOrders: blockOrdersData } =
      buildingPayload
    if (building.levels.length === 0 && buildingData.length > 0) {
      dispatch(
        setBuilding({
          ...building,
          levels: buildingData,
          blockOrders: blockOrdersData,
          activeLevel: buildingData[0]?.level,
        })
      )
    }

    if (
      buildingData.length > 0 &&
      buildingPayload.fulfilledTimeStamp !== lastDispatchedTimestamp.current
    ) {
      lastDispatchedTimestamp.current = buildingPayload.fulfilledTimeStamp
      dispatch(
        setBuilding({
          ...building,
          levels: buildingData,
          blockOrders: blockOrdersData,
        })
      )
    }
  }, [buildingPayload, building])

  React.useEffect(() => {
    if (viewer) {
      viewer.addListener('sceneLoaded', prepareSnapDataOnScreenLoad)
      viewer.setControlsVisible(false)

      window.parent.addEventListener('message', handleOnLevelClick)
    }
  }, [viewer, building])

  React.useEffect(() => {
    if (session) {
      firebaseControlQuery.update({
        [`snaploader.isLoaded`]: isSnapDataLoaded,
        [`snaploader.data.views`]: getViewList(),
        [`snaploader.data.levels`]: getLevelList(),
      })
    }
  }, [snapData])

  React.useEffect(() => {
    const { levels } = building
    if (levels.length < 1) return

    resetHighlightedNodes()

    const { apply } = unitFilter

    if (!apply) return

    const filteredLevelList = getFilteredData()
    highlightLevels(filteredLevelList)
  }, [unitFilter, activeBlock])

  React.useEffect(() => {
    const interval = setInterval(() => {
      handleLoadingState(interval)
    }, 200)

    return () => {
      clearInterval(interval)
    }
  }, [])

  React.useEffect(() => {
    if (session) {
      const {
        connected,
        snaploader: {
          rotateCamera: rotateCameraFirebase,
          activeView: activeViewFirebase,
          activeBlock: activeBlockFirebase,
        },
        building: { unitFilter: unitFilterFirebase },
      } = session

      setIsSessionConnected(connected)
      setActiveBlock(activeBlockFirebase)
      if (activeViewFirebase && activeViewFirebase !== activeView) {
        setActiveView(activeViewFirebase)
        setView(activeViewFirebase)
      }
      autoRotateCamera(rotateCameraFirebase)
      dispatch(setFilter(unitFilterFirebase))
    }
  }, [session])

  return (
    <Container>
      <DataHandler
        payloads={{
          ...buildingPayload,
          data: building.levels,
        }}
      >
        {snaploaderSDKLoadError ? (
          <DataNotFound message="Failed to load SnapLoader SDK" />
        ) : (
          isSnapDataLoaded &&
          !isSessionConnected && <div>This session is connected</div>
        )}
        <div ref={containerRef}></div>
      </DataHandler>
    </Container>
  )
}

export default connect(
  ({
    firestore: { session },
    projectIdentity,
    projectConfig: {
      snaploaderConfiguration,
      hideFilter,
      showPrice,
      statusLabels,
    },
    building,
    unitFilter,
  }: RootStateTypeExtra) => ({
    session,
    projectIdentity,
    snaploaderConfiguration,
    building,
    hideFilter,
    showPrice,
    unitFilter,
    availableStatusLabel: statusLabels?.available,
  })
)(SnaploaderView)
